import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container,
  Input, 
  Select,
  Button,
  VStack, 
  HStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Spinner,
  Textarea,
  Box,
  Text,
  CheckboxGroup,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Flex,
  Switch,
  InputGroup,
  InputRightElement,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
  NumberInput,
  NumberInputField,
  Stack,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Code,
  FormHelperText
} from '@chakra-ui/react';
import { FiSave, FiInfo, FiPlus, FiX } from 'react-icons/fi';
import { ArrowBackIcon, ChevronRightIcon, InfoIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const CreateContent = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'));
  const tenantId = user?.tenant_ids?.[0] || null;

  // Form state for multilingual content
  const [content, setContent] = useState({
    tenant_id: tenantId,
    site_id: '',
    slug: '',
    content_model_id: '',
    status: 'draft',
    translations: [
      {
        locale_id: '',
        title: '',
        content: '',
        excerpt: '',
        meta_title: '',
        meta_description: '',
        custom_fields: {},
        status: 'draft'
      }
    ]
  });

  // Dropdown options state
  const [sites, setSites] = useState([]);
  const [locales, setLocales] = useState([]);
  const [contentModels, setContentModels] = useState([]);
  const [selectedModelFields, setSelectedModelFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Taxonomy state
  const [modelTaxonomies, setModelTaxonomies] = useState([]);
  const [taxonomyTerms, setTaxonomyTerms] = useState({});
  const [selectedTerms, setSelectedTerms] = useState({});
  const [loadingTaxonomies, setLoadingTaxonomies] = useState(false);
  const [loadingModelFields, setLoadingModelFields] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({});

  // Tags input state
  const [tagInput, setTagInput] = useState({});

  useEffect(() => {
    if (!tenantId) {
      toast({
        title: "Authentication error",
        description: "Unable to find tenant information. Please log in again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    fetchInitialData();
  }, [tenantId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      const [sitesData, localesData, contentModelsData] = await Promise.all([
        apiClient(`/sites/${tenantId}`),
        apiClient(`/locales/${tenantId}`),
        apiClient(`/content-models/${tenantId}`)
      ]);

      setSites(Array.isArray(sitesData) ? sitesData : []);
      setLocales(Array.isArray(localesData) ? localesData : []);
      setContentModels(Array.isArray(contentModelsData) ? contentModelsData : []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast({
        title: 'Error fetching data',
        description: error.message || 'Failed to load initial data',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContentModelFields = async (modelId) => {
    if (!modelId) {
      setSelectedModelFields([]);
      return;
    }

    try {
      setLoadingModelFields(true);
      
      const response = await apiClient(`/content-models/${tenantId}/${modelId}`);
      
      // Process content model fields
      const fieldsData = Array.isArray(response.fields) 
        ? response.fields.map(field => ({
            key: field.name.toLowerCase().replace(/\s+/g, '_'),
            label: field.name,
            type: field.type,
            options: field.options || []
          }))
        : [];
      
      setSelectedModelFields(fieldsData);
      
      // Initialize custom_fields in the first translation
      const initialCustomFields = fieldsData.reduce((acc, field) => {
        let defaultValue = '';
        
        if (field.type === 'boolean') defaultValue = false;
        else if (field.type === 'number') defaultValue = '';
        else if (field.type === 'richtext') defaultValue = '';
        else if (field.type === 'json') defaultValue = '{}';
        else if (field.type === 'tags') defaultValue = [];
        else if (field.type === 'multiselect') defaultValue = [];
        
        return {
          ...acc,
          [field.key]: defaultValue
        };
      }, {});
      
      // Update the first translation's custom_fields
      setContent(prev => ({
        ...prev,
        translations: prev.translations.map((translation, index) => 
          index === 0 
            ? { ...translation, custom_fields: initialCustomFields } 
            : translation
        )
      }));
      
    } catch (error) {
      console.error('Error fetching model fields:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content model field definitions',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      setSelectedModelFields([]);
    } finally {
      setLoadingModelFields(false);
    }
  };
  
  const fetchTaxonomiesForModel = async (modelId) => {
    if (!modelId) {
      setModelTaxonomies([]);
      setTaxonomyTerms({});
      setSelectedTerms({});
      return;
    }

    try {
      setLoadingTaxonomies(true);

      // First, get the content model to get assigned taxonomy IDs
      const modelResponse = await apiClient(`/content-models/${tenantId}/${modelId}`);
      
      if (!modelResponse.taxonomy_ids || !Array.isArray(modelResponse.taxonomy_ids) || modelResponse.taxonomy_ids.length === 0) {
        setModelTaxonomies([]);
        setTaxonomyTerms({});
        setSelectedTerms({});
        return;
      }
      
      const taxonomyIds = modelResponse.taxonomy_ids;
      
      // Reset selected terms when changing model
      setSelectedTerms({});
      
      // Fetch all taxonomies to get their details
      const allTaxonomiesResponse = await apiClient(`/taxonomies/${tenantId}`);
      
      let allTaxonomies = [];
      
      if (Array.isArray(allTaxonomiesResponse)) {
        allTaxonomies = allTaxonomiesResponse;
      } else if (allTaxonomiesResponse && allTaxonomiesResponse.taxonomies) {
        allTaxonomies = allTaxonomiesResponse.taxonomies;
      }
      
      // Filter to only the taxonomies assigned to this model
      const filteredTaxonomies = allTaxonomies.filter(tax => 
        taxonomyIds.includes(tax.id) || taxonomyIds.includes(String(tax.id))
      );
      
      setModelTaxonomies(filteredTaxonomies);
      
      // For each taxonomy, fetch its terms
      const termsPromises = filteredTaxonomies.map(async (tax) => {
        try {
          const termsResponse = await apiClient(`/taxonomies/${tenantId}/${tax.id}/terms`);
          
          let terms = [];
          if (Array.isArray(termsResponse)) {
            terms = termsResponse;
          } else if (termsResponse && termsResponse.terms) {
            terms = termsResponse.terms;
          }
          
          return { taxonomyId: tax.id, terms };
        } catch (error) {
          console.error(`Error fetching terms for taxonomy ${tax.id}:`, error);
          return { taxonomyId: tax.id, terms: [] };
        }
      });
      
      const termsResults = await Promise.all(termsPromises);
      
      // Build taxonomyTerms object with taxonomy IDs as keys
      const termsMap = {};
      termsResults.forEach(result => {
        termsMap[result.taxonomyId] = result.terms;
      });
      
      setTaxonomyTerms(termsMap);
    } catch (error) {
      console.error('Error fetching taxonomies for model:', error);
      toast({
        title: 'Error',
        description: 'Failed to load taxonomies for this content model',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      setModelTaxonomies([]);
      setTaxonomyTerms({});
    } finally {
      setLoadingTaxonomies(false);
    }
  };

  useEffect(() => {
    const updateForContentModel = async () => {
      if (content.content_model_id) {
        await Promise.all([
          fetchContentModelFields(content.content_model_id),
          fetchTaxonomiesForModel(content.content_model_id)
        ]);
      } else {
        setSelectedModelFields([]);
        setModelTaxonomies([]);
        setTaxonomyTerms({});
        setSelectedTerms({});
      }
    };
    
    updateForContentModel();
  }, [content.content_model_id]);

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-generate slug from first translation title
    if (name === 'title' && content.translations.length > 0) {
      const slug = generateSlug(value);
      setContent(prev => ({
        ...prev,
        slug: slug,
        translations: prev.translations.map((translation, index) => 
          index === 0 
            ? { ...translation, title: value }
            : translation
        )
      }));
    } else {
      setContent({ ...content, [name]: value });
    }
    
    // Clear validation errors when field is changed
    if (errors[name]) {
      setErrors({...errors, [name]: null});
    }
  };

  const handleTranslationChange = (translationIndex, field, value) => {
    setContent(prev => ({
      ...prev,
      translations: prev.translations.map((translation, index) => 
        index === translationIndex 
          ? { ...translation, [field]: value }
          : translation
      )
    }));
  };

  const handleCustomFieldChange = (translationIndex, fieldKey, value) => {
    setContent(prev => ({
      ...prev,
      translations: prev.translations.map((translation, index) => 
        index === translationIndex 
          ? { 
              ...translation, 
              custom_fields: {
                ...translation.custom_fields,
                [fieldKey]: value
              }
            }
          : translation
      )
    }));
  };

  // Handle tags field
  const handleTagsChange = (translationIndex, fieldKey, tags) => {
    handleCustomFieldChange(translationIndex, fieldKey, tags);
  };

  const addTag = (translationIndex, fieldKey) => {
    const inputKey = `${translationIndex}_${fieldKey}`;
    const inputValue = tagInput[inputKey];
    if (inputValue && inputValue.trim()) {
      const currentTags = content.translations[translationIndex].custom_fields[fieldKey] || [];
      if (!currentTags.includes(inputValue.trim())) {
        handleTagsChange(translationIndex, fieldKey, [...currentTags, inputValue.trim()]);
      }
      setTagInput(prev => ({ ...prev, [inputKey]: '' }));
    }
  };

  const removeTag = (translationIndex, fieldKey, tagToRemove) => {
    const currentTags = content.translations[translationIndex].custom_fields[fieldKey] || [];
    handleTagsChange(translationIndex, fieldKey, currentTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputChange = (translationIndex, fieldKey, value) => {
    const inputKey = `${translationIndex}_${fieldKey}`;
    setTagInput(prev => ({ ...prev, [inputKey]: value }));
  };

  const handleTagInputKeyPress = (translationIndex, fieldKey, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(translationIndex, fieldKey);
    }
  };
  
  const handleTermsChange = (taxonomyId, selectedTermIds) => {
    const taxIdStr = taxonomyId.toString();
    setSelectedTerms(prev => ({
      ...prev,
      [taxIdStr]: selectedTermIds
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!content.site_id) newErrors.site_id = "Site is required";
    if (!content.content_model_id) newErrors.content_model_id = "Content model is required";
    if (!content.slug) newErrors.slug = "Slug is required";
    
    // At least one translation is required
    if (!content.translations || content.translations.length === 0) {
      newErrors.translations = "At least one translation is required";
    } else {
      // Check first translation has required fields
      const firstTranslation = content.translations[0];
      if (!firstTranslation.locale_id) newErrors.locale_id = "Locale is required for the first translation";
      if (!firstTranslation.title) newErrors.title = "Title is required for the first translation";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast({
        title: "Validation Error",
        description: "Please fill out all required fields",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setIsSubmitting(true);

      // Prepare taxonomy terms for submission
      const taxonomyData = [];
      
      Object.entries(selectedTerms).forEach(([taxonomyId, termIds]) => {
        if (!termIds || termIds.length === 0) return;
        
        const taxonomy = modelTaxonomies.find(tax => 
          tax.id.toString() === taxonomyId.toString()
        );
        
        if (!taxonomy) return;
        
        // Get the terms by ID
        const termsForTaxonomy = taxonomyTerms[taxonomyId] || [];
        const selectedTermNames = termIds
          .map(id => {
            const term = termsForTaxonomy.find(t => t.id.toString() === id.toString());
            return term ? term.name : null;
          })
          .filter(Boolean);
        
        if (selectedTermNames.length > 0) {
          taxonomyData.push({
            name: taxonomy.name,
            value: selectedTermNames
          });
        }
      });

      // Convert IDs to integers and ensure proper data types
      const processedTranslations = content.translations
        .filter(t => t.locale_id && t.title) // Only include valid translations
        .map(translation => ({
          ...translation,
          locale_id: parseInt(translation.locale_id, 10), // Convert to integer
          custom_fields: translation.custom_fields || {}
        }));

      // Create submission payload with proper data types
      const submitData = {
        tenant_id: parseInt(tenantId, 10),
        site_id: parseInt(content.site_id, 10),
        slug: content.slug,
        content_model_id: parseInt(content.content_model_id, 10),
        translations: processedTranslations,
        taxonomies: taxonomyData
      };

      console.log('Submitting data:', JSON.stringify(submitData, null, 2));
      
      const response = await apiClient('/content-entries', {
        method: 'POST',
        body: JSON.stringify(submitData)
      });
      
      toast({
        title: 'Success',
        description: 'Content created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      navigate('/content');
    } catch (error) {
      console.error('Error creating content:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create content',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCustomField = (field, translationIndex) => {
    const currentValue = content.translations[translationIndex]?.custom_fields?.[field.key] || '';

    switch (field.type) {
      case 'text':
        return (
          <Input
            value={currentValue}
            onChange={(e) => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={currentValue}
            onChange={(e) => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            minH="120px"
            resize="vertical"
          />
        );

      case 'richtext':
        return (
          <VStack align="stretch" spacing={2}>
            <Textarea
              value={currentValue}
              onChange={(e) => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
              placeholder={`Enter rich text content for ${field.label}`}
              minH="200px"
              resize="vertical"
              fontFamily="monospace"
              fontSize="sm"
            />
            <FormHelperText>
              Rich text editor - supports HTML markup
            </FormHelperText>
          </VStack>
        );

      case 'tags':
        const inputKey = `${translationIndex}_${field.key}`;
        return (
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Input
                value={tagInput[inputKey] || ''}
                onChange={(e) => handleTagInputChange(translationIndex, field.key, e.target.value)}
                onKeyPress={(e) => handleTagInputKeyPress(translationIndex, field.key, e)}
                placeholder={`Add tags for ${field.label}`}
              />
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiPlus />}
                onClick={() => addTag(translationIndex, field.key)}
                isDisabled={!tagInput[inputKey]?.trim()}
              >
                Add
              </Button>
            </HStack>
            
            {Array.isArray(currentValue) && currentValue.length > 0 && (
              <Box>
                <Text fontSize="sm" mb={2} fontWeight="medium">Tags:</Text>
                <Wrap>
                  {currentValue.map(tag => (
                    <WrapItem key={tag}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        colorScheme="blue"
                      >
                        <TagLabel>{tag}</TagLabel>
                        <TagCloseButton
                          onClick={() => removeTag(translationIndex, field.key, tag)}
                        />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
            <FormHelperText>
              Press Enter or click Add to add tags
            </FormHelperText>
          </VStack>
        );

      default:
        return (
          <Input
            value={currentValue}
            onChange={(e) => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl">
        <VStack align="center" justify="center" height="50vh">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/content">Content</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create Content</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Create Multilingual Content</Heading>
        <HStack spacing={4}>
          <Button 
            as={RouterLink}
            to="/content"
            leftIcon={<ArrowBackIcon />}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            leftIcon={<FiSave />}
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Creating..."
            isDisabled={
              !content.site_id || 
              !content.content_model_id || 
              !content.slug ||
              !content.translations[0]?.locale_id ||
              !content.translations[0]?.title
            }
          >
            Create Content
          </Button>
        </HStack>
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Basic Information</Tab>
          <Tab>Content & Translation</Tab>
          {modelTaxonomies.length > 0 && <Tab>Taxonomies</Tab>}
        </TabList>

        <TabPanels>
          {/* Basic Information Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <VStack align="stretch" spacing={5}>
                <Card borderColor={borderColor} variant="outline">
                  <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                    <Heading size="sm">Content Information</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired isInvalid={errors.title}>
                        <FormLabel fontSize="sm">Title (First Translation)</FormLabel>
                        <InputGroup>
                          <Input 
                            name="title" 
                            value={content.translations[0]?.title || ''} 
                            onChange={(e) => handleChange({ target: { name: 'title', value: e.target.value } })} 
                            placeholder="Enter content title"
                            focusBorderColor="blue.400"
                          />
                          {content.translations[0]?.title && (
                            <InputRightElement>
                              <Box color="green.500">✓</Box>
                            </InputRightElement>
                          )}
                        </InputGroup>
                        {errors.title && <Text color="red.500" fontSize="sm" mt={1}>{errors.title}</Text>}
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.slug}>
                        <FormLabel fontSize="sm">Slug</FormLabel>
                        <Input 
                          name="slug" 
                          value={content.slug} 
                          onChange={handleChange} 
                          placeholder="content-slug"
                          focusBorderColor="blue.400"
                        />
                        {errors.slug && <Text color="red.500" fontSize="sm" mt={1}>{errors.slug}</Text>}
                        <FormHelperText>Auto-generated from title. Edit if needed.</FormHelperText>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel fontSize="sm">Status</FormLabel>
                        <Select
                          name="status"
                          value={content.status}
                          onChange={handleChange}
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                        </Select>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>

              <VStack align="stretch" spacing={5}>
                <Card borderColor={borderColor} variant="outline">
                  <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                    <Heading size="sm">Site & Model</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired isInvalid={errors.site_id}>
                        <FormLabel fontSize="sm">Site</FormLabel>
                        <Select 
                          name="site_id" 
                          value={content.site_id} 
                          onChange={handleChange}
                          placeholder="Select Site"
                        >
                          {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.name}
                            </option>
                          ))}
                        </Select>
                        {errors.site_id && <Text color="red.500" fontSize="sm" mt={1}>{errors.site_id}</Text>}
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.locale_id}>
                        <FormLabel fontSize="sm">Primary Locale</FormLabel>
                        <Select 
                          value={content.translations[0]?.locale_id || ''} 
                          onChange={(e) => handleTranslationChange(0, 'locale_id', e.target.value)}
                          placeholder="Select Locale"
                        >
                          {locales.map((locale) => (
                            <option key={locale.id} value={locale.id}>
                              {locale.name} ({locale.code})
                            </option>
                          ))}
                        </Select>
                        {errors.locale_id && <Text color="red.500" fontSize="sm" mt={1}>{errors.locale_id}</Text>}
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.content_model_id}>
                        <FormLabel fontSize="sm">Content Model</FormLabel>
                        <Select
                          name="content_model_id"
                          value={content.content_model_id}
                          onChange={handleChange}
                          placeholder="Select Content Model"
                        >
                          {contentModels.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name}
                            </option>
                          ))}
                        </Select>
                        {errors.content_model_id && (
                          <Text color="red.500" fontSize="sm" mt={1}>{errors.content_model_id}</Text>
                        )}
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </SimpleGrid>
          </TabPanel>

          {/* Content & Translation Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading size="md">Primary Translation</Heading>
              
              <Card borderColor={borderColor} variant="outline">
                <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                  <HStack justify="space-between">
                    <Heading size="sm">
                      Translation: {locales.find(l => l.id.toString() === content.translations[0]?.locale_id?.toString())?.name || 'Select Locale'}
                    </Heading>
                    {content.translations[0]?.locale_id && (
                      <Badge colorScheme="blue">
                        {locales.find(l => l.id.toString() === content.translations[0]?.locale_id?.toString())?.code}
                      </Badge>
                    )}
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Content</FormLabel>
                        <Textarea
                          value={content.translations[0]?.content || ''}
                          onChange={(e) => handleTranslationChange(0, 'content', e.target.value)}
                          placeholder="Enter main content"
                          minH="150px"
                          resize="vertical"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">Excerpt</FormLabel>
                        <Textarea
                          value={content.translations[0]?.excerpt || ''}
                          onChange={(e) => handleTranslationChange(0, 'excerpt', e.target.value)}
                          placeholder="Enter excerpt"
                          minH="100px"
                          resize="vertical"
                        />
                      </FormControl>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Meta Title</FormLabel>
                        <Input
                          value={content.translations[0]?.meta_title || ''}
                          onChange={(e) => handleTranslationChange(0, 'meta_title', e.target.value)}
                          placeholder="Enter meta title"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">Meta Description</FormLabel>
                        <Textarea
                          value={content.translations[0]?.meta_description || ''}
                          onChange={(e) => handleTranslationChange(0, 'meta_description', e.target.value)}
                          placeholder="Enter meta description"
                          resize="vertical"
                        />
                      </FormControl>
                    </SimpleGrid>

                    {/* Custom Fields */}
                    {selectedModelFields.length > 0 && (
                      <Box>
                        <Heading size="sm" mb={4}>Custom Fields</Heading>
                        <Accordion allowMultiple>
                          {selectedModelFields.map((field, index) => (
                            <AccordionItem key={field.key} borderWidth="1px" borderRadius="md" mb={3}>
                              <h2>
                                <AccordionButton py={3}>
                                  <HStack flex="1" textAlign="left" spacing={3}>
                                    <Text fontWeight="medium">{field.label}</Text>
                                    <Badge colorScheme="blue" fontSize="xs">{field.type}</Badge>
                                  </HStack>
                                  <AccordionIcon />
                                </AccordionButton>
                              </h2>
                              <AccordionPanel pb={4}>
                                <FormControl>
                                  <FormLabel fontSize="sm" fontWeight="medium">
                                    {field.label}
                                  </FormLabel>
                                  {renderCustomField(field, 0)}
                                </FormControl>
                              </AccordionPanel>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* Taxonomies Tab */}
          {modelTaxonomies.length > 0 && (
            <TabPanel>
              <VStack spacing={5} align="stretch">
                <Heading size="md">Taxonomies</Heading>
                
                {loadingTaxonomies ? (
                  <Flex justify="center" py={10}>
                    <Spinner />
                    <Text ml={3}>Loading taxonomies...</Text>
                  </Flex>
                ) : (
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {modelTaxonomies.map(taxonomy => (
                      <Card key={taxonomy.id} borderColor={borderColor} variant="outline">
                        <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                          <Heading size="sm">{taxonomy.name}</Heading>
                        </CardHeader>
                        <CardBody>
                          {taxonomyTerms[taxonomy.id] && taxonomyTerms[taxonomy.id].length > 0 ? (
                            <>
                              {selectedTerms[taxonomy.id]?.length > 0 && (
                                <Flex wrap="wrap" mb={4} gap={2}>
                                  {selectedTerms[taxonomy.id].map(termId => {
                                    const term = taxonomyTerms[taxonomy.id].find(t => t.id.toString() === termId);
                                    return term ? (
                                      <Badge key={termId} colorScheme="blue" py={1} px={2}>
                                        {term.name}
                                      </Badge>
                                    ) : null;
                                  })}
                                </Flex>
                              )}
                              
                              <CheckboxGroup 
                                colorScheme="blue"
                                value={selectedTerms[taxonomy.id] || []}
                                onChange={(values) => handleTermsChange(taxonomy.id, values)}
                              >
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                  {taxonomyTerms[taxonomy.id].map(term => (
                                    <Checkbox 
                                      key={term.id} 
                                      value={term.id.toString()}
                                      mb={2}
                                    >
                                      {term.name}
                                    </Checkbox>
                                  ))}
                                </SimpleGrid>
                              </CheckboxGroup>
                            </>
                          ) : (
                            <Text color="gray.500">No terms available for this taxonomy</Text>
                          )}
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>
                )}
              </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
      
      {/* Bottom Action Bar */}
      <Flex justify="flex-end" mt={8} mb={4} borderTopWidth="1px" pt={5}>
        <HStack spacing={4}>
          <Button 
            variant="outline" 
            as={RouterLink}
            to="/content"
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Creating..."
            leftIcon={<FiSave />}
            isDisabled={
              !content.site_id || 
              !content.content_model_id || 
              !content.slug ||
              !content.translations[0]?.locale_id ||
              !content.translations[0]?.title
            }
          >
            Create Content
          </Button>
        </HStack>
      </Flex>
    </Container>
  );
};

export default CreateContent;
